// src/pages/orders/DeliveryFollowUpPage.jsx
import React, { useState, useEffect } from 'react';
import {
  Truck,
  User,
  MapPin,
  Calendar,
  Phone as PhoneIcon,
  CheckCircle,
  Clock,
  XCircle,
  RefreshCw,
  AlertCircle,
  Copy,
  Check,
  Columns,
  History
} from 'lucide-react';
import { PageHeader, DataTable } from '../../components/common';
import { getEcomOrders, getDeliveryFollowUpNoteCounts } from '../../services/api/orders';
import { getCourierStatuses } from '../../services/api/businessCourier';
import { useBusinessSettings } from '../../hooks/useBusinessSettings';
import { useAlert } from '../../contexts/AlertContext';
import { useLocalization } from '../../contexts/LocalizationContext';
import { formatCurrency } from '../../utils/orderCalculations';
import { formatDate, formatTime } from '../../utils/dateUtils';
import DeliveryFollowUpStatusModal from './components/DeliveryFollowUpStatusModal';
import AddDeliveryFollowUpModal from './components/AddDeliveryFollowUpModal';
import DeliveryFollowUpHistoryModal from './components/DeliveryFollowUpHistoryModal';
import AirwaybillTrackingModal from './components/AirwaybillTrackingModal';
import orderStyles from './OrderListPage.module.css';
import styles from './DeliveryFollowUpPage.module.css';

const calculateAgeInDays = (dispatchedDate) => {
  if (!dispatchedDate) return null;
  const dispatched = new Date(dispatchedDate);
  const today = new Date();
  dispatched.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  const diffMs = today.getTime() - dispatched.getTime();
  return Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
};

const DeliveryFollowUpPage = () => {
  const { settings, isLoading: settingsLoading, saveSettings } = useBusinessSettings('deliveryFollowUp');
  const { success, error: showError } = useAlert();
  const { t } = useLocalization();

  const [orders, setOrders]             = useState([]);
  const [isLoading, setIsLoading]       = useState(true);
  const [loadingError, setLoadingError] = useState(null);
  const [copiedStates, setCopiedStates] = useState({});

  const [courierStatuses, setCourierStatuses]   = useState([]);
  const [showStatusModal, setShowStatusModal]   = useState(false);
  const [isSavingSettings, setIsSavingSettings] = useState(false);

  const [noteCounts, setNoteCounts]           = useState({});
  const [addFollowUpOrder, setAddFollowUpOrder] = useState(null);
  const [historyOrder, setHistoryOrder]         = useState(null);
  const [trackingOrder, setTrackingOrder]       = useState(null);

  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    total: 0,
    pageSizeOptions: [10, 20, 50, 100, 500, 1000]
  });

  const [sorting, setSorting] = useState({
    orderBy: 'dispatchedDate',
    orderDirection: 'ASC'
  });

  useEffect(() => {
    getCourierStatuses()
      .then(res => setCourierStatuses(res.courierStatuses || []))
      .catch(err => console.error('Failed to load courier statuses:', err));
  }, []);

  useEffect(() => {
    if (settingsLoading) return;
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsLoading, settings, pagination.currentPage, pagination.pageSize, sorting.orderBy, sorting.orderDirection]);

  const selectedCourierStatusIds = Array.isArray(settings.courierStatusIds)
    ? settings.courierStatusIds.filter(Boolean)
    : [];

  const fetchOrders = async () => {
    try {
      setIsLoading(true);
      setLoadingError(null);

      const params = {
        page: pagination.currentPage,
        limit: pagination.pageSize,
        orderBy: sorting.orderBy,
        orderDirection: sorting.orderDirection
      };

      if (selectedCourierStatusIds.length > 0) {
        params.courierStatusIds = selectedCourierStatusIds.join(',');
      } else {
        params.isDispatched = true;
        params.isClosed = false;
      }

      const response = await getEcomOrders(params);
      const fetchedOrders = response.orders || [];
      setOrders(fetchedOrders);
      setPagination(prev => ({ ...prev, total: response.total || 0 }));
      loadNoteCounts(fetchedOrders.map(o => o.id));
    } catch (err) {
      console.error('Failed to fetch delivery follow-up orders:', err);
      setLoadingError(err.response?.status === 403
        ? 'You do not have permission to view these orders.'
        : 'Failed to load orders.');
    } finally {
      setIsLoading(false);
    }
  };

  const loadNoteCounts = async (orderIds) => {
    if (!orderIds || orderIds.length === 0) return;
    try {
      const response = await getDeliveryFollowUpNoteCounts(orderIds);
      setNoteCounts(prev => ({ ...prev, ...(response.counts || {}) }));
    } catch (err) {
      console.error('Failed to load delivery follow-up note counts:', err);
    }
  };

  const handleOpenAddFollowUp = (order) => setAddFollowUpOrder(order);
  const handleCloseAddFollowUp = () => setAddFollowUpOrder(null);

  const handleFollowUpNoteSaved = () => {
    if (addFollowUpOrder) {
      setNoteCounts(prev => ({
        ...prev,
        [addFollowUpOrder.id]: (prev[addFollowUpOrder.id] || 0) + 1
      }));
    }
    setAddFollowUpOrder(null);
    success('Delivery follow-up saved');
  };

  const handleOpenHistory = (order) => setHistoryOrder(order);
  const handleCloseHistory = () => setHistoryOrder(null);

  const handleCopyToClipboard = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedStates(prev => ({ ...prev, [key]: true }));
      setTimeout(() => setCopiedStates(prev => ({ ...prev, [key]: false })), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      showError('Failed to copy to clipboard');
    }
  };

  const handleSaveStatusSettings = async (statusIds) => {
    try {
      setIsSavingSettings(true);
      await saveSettings({ ...settings, courierStatusIds: statusIds });
      setShowStatusModal(false);
      setPagination(prev => ({ ...prev, currentPage: 1 }));
      success('Delivery follow-up statuses saved');
    } catch (err) {
      console.error('Failed to save delivery follow-up settings:', err);
      showError('Failed to save statuses');
    } finally {
      setIsSavingSettings(false);
    }
  };

  const getOrderStatusIcon = (statusKey) => {
    const icons = {
      pending:       <Clock size={14} />,
      confirmed:     <CheckCircle size={14} />,
      hold:          <Clock size={14} />,
      not_confirmed: <XCircle size={14} />,
      cancelled:     <XCircle size={14} />,
      in_courier:    <Truck size={14} />,
      settled:       <CheckCircle size={14} />,
      returned:      <XCircle size={14} />
    };
    return icons[statusKey] || <Clock size={14} />;
  };

  const truncateText = (text, maxLength) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const columns = [
    {
      key: 'createdAt',
      header: 'Created At',
      sortable: true,
      width: '150px',
      render: (order) => (
        <div className={orderStyles.dateCell}>
          <div className={orderStyles.dateInfo}><Calendar size={12} />{formatDate(order.createdAt)}</div>
          <div className={orderStyles.timeInfo}>{formatTime(order.createdAt)}</div>
        </div>
      )
    },
    {
      key: 'customerName',
      header: 'Customer',
      sortable: true,
      width: '220px',
      render: (order) => {
        const primaryPhone = order.phoneNumbers?.find(p => p.isPrimary) || order.phoneNumbers?.[0];
        const copyKey  = `phone-${order.id}`;
        const isCopied = copiedStates[copyKey];

        return (
          <div className={orderStyles.customerInfo}>
            <div className={orderStyles.customerName}><User size={14} />{truncateText(order.customerName, 20)}</div>
            <div className={orderStyles.customerAddress}><MapPin size={12} />{order.City?.name || 'N/A'}</div>
            {primaryPhone && (
              <div className={orderStyles.phoneNumberRow}>
                <a href={`tel:${primaryPhone.phoneNumber}`} className={orderStyles.phoneNumber} onClick={(e) => e.stopPropagation()}>
                  <PhoneIcon size={12} />{primaryPhone.phoneNumber}
                </a>
                <button className={orderStyles.copyButton} onClick={(e) => { e.stopPropagation(); handleCopyToClipboard(primaryPhone.phoneNumber, copyKey); }} title={isCopied ? 'Copied!' : 'Copy phone number'}>
                  {isCopied ? <Check size={12} /> : <Copy size={12} />}
                </button>
              </div>
            )}
          </div>
        );
      }
    },
    {
      key: 'courier',
      header: 'Courier',
      width: '180px',
      render: (order) => {
        const copyKey  = `awb-${order.id}`;
        const isCopied = copiedStates[copyKey];

        return (
          <div className={orderStyles.courierInfo}>
            {order.airwaybillNumber && (
              <div className={orderStyles.airwaybillWrapper}>
                <button
                  type="button"
                  className={orderStyles.airwaybillNumber}
                  onClick={(e) => { e.stopPropagation(); setTrackingOrder(order); }}
                  title={t('ecomOrders.viewPackageTimeline')}
                >
                  {order.airwaybillNumber}
                </button>
                <button className={orderStyles.copyButton} onClick={(e) => { e.stopPropagation(); handleCopyToClipboard(order.airwaybillNumber, copyKey); }} title={isCopied ? 'Copied!' : 'Copy airwaybill number'}>
                  {isCopied ? <Check size={12} /> : <Copy size={12} />}
                </button>
              </div>
            )}
            {order.assignedCourierAccount ? (
              <div className={orderStyles.courierName}><Truck size={12} />{order.assignedCourierAccount.accountName}</div>
            ) : (
              <div className={orderStyles.courierName}><Truck size={12} />N/A</div>
            )}
          </div>
        );
      }
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      width: '160px',
      render: (order) => (
        <div className={orderStyles.statusCell}>
          <div
            className={orderStyles.statusBadge}
            style={{
              backgroundColor: order.OrderStatus?.colorCode ? `${order.OrderStatus.colorCode}20` : '#F59E0B20',
              borderColor: order.OrderStatus?.colorCode || '#F59E0B',
              color: order.OrderStatus?.colorCode || '#92400e'
            }}
          >
            {getOrderStatusIcon(order.OrderStatus?.key)}
            <span>{order.OrderStatus?.label || order.orderStatusKey}</span>
          </div>
          {order.courierStatus && (
            <div className={orderStyles.courierStatusBadge} title={order.courierStatus.statusLabel}>
              <span className={orderStyles.courierStatusText}>{order.courierStatus.statusLabel}</span>
            </div>
          )}
        </div>
      )
    },
    {
      key: 'grandTotal',
      header: 'Total',
      sortable: true,
      width: '120px',
      align: 'right',
      render: (order) => <div className={orderStyles.totalAmount}>{formatCurrency(order.grandTotal)}</div>
    },
    {
      key: 'orderNumber',
      header: 'Order Number',
      sortable: true,
      width: '140px',
      render: (order) => {
        const copyKey  = `order-${order.id}`;
        const isCopied = copiedStates[copyKey];

        return (
          <div className={orderStyles.orderNumberCell}>
            <div className={orderStyles.orderNumber}>#{order.orderNumber}</div>
            <button className={orderStyles.copyButton} onClick={(e) => { e.stopPropagation(); handleCopyToClipboard(order.orderNumber, copyKey); }} title={isCopied ? 'Copied!' : 'Copy order number'}>
              {isCopied ? <Check size={14} /> : <Copy size={14} />}
            </button>
          </div>
        );
      }
    },
    {
      key: 'age',
      header: 'Age',
      width: '110px',
      render: (order) => {
        const age = calculateAgeInDays(order.dispatchedDate);
        return (
          <div className={styles.ageCell}>
            {age === null ? <span className={orderStyles.naText}>—</span> : `${age} ${age === 1 ? 'Day' : 'Days'}`}
          </div>
        );
      }
    }
  ];

  const tableActions = {
    render: (order) => {
      const count = noteCounts[order.id] || 0;
      return (
        <div className={orderStyles.actionsWrapper}>
          <button
            className={styles.actionIconButton}
            onClick={(e) => { e.stopPropagation(); handleOpenAddFollowUp(order); }}
            title="Add Delivery Follow-up"
          >
            <PhoneIcon size={14} />
          </button>
          <button
            className={styles.actionIconButton}
            onClick={(e) => { e.stopPropagation(); handleOpenHistory(order); }}
            title="Follow-up History"
          >
            <History size={14} />
            {count > 0 && <span className={styles.actionIconBadge}>{count}</span>}
          </button>
        </div>
      );
    },
    width: '100px',
    frozen: true
  };

  if ((isLoading || settingsLoading) && orders.length === 0) {
    return (
      <div className={styles.deliveryFollowUpPage}>
        <PageHeader title="Delivery Follow-up" description="Track dispatched orders awaiting delivery" icon={Truck} />
        <div className={orderStyles.loadingState}>
          <div className={orderStyles.loadingSpinnerLarge}></div>
          <h3>Loading</h3>
          <p>Loading orders...</p>
        </div>
      </div>
    );
  }

  if (loadingError) {
    return (
      <div className={styles.deliveryFollowUpPage}>
        <PageHeader title="Delivery Follow-up" description="Track dispatched orders awaiting delivery" icon={Truck} />
        <div className={orderStyles.errorState}>
          <AlertCircle className={orderStyles.errorIcon} size={48} />
          <h3>Unable to load orders</h3>
          <p>{loadingError}</p>
          <button onClick={fetchOrders} className={orderStyles.retryButton}><RefreshCw size={16} />Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.deliveryFollowUpPage}>
      <PageHeader
        title="Delivery Follow-up"
        description="Track dispatched orders awaiting delivery"
        icon={Truck}
        actions={
          <button
            onClick={() => setShowStatusModal(true)}
            className={`${styles.mappingButton} ${selectedCourierStatusIds.length > 0 ? styles.mappingButtonActive : ''}`}
            title="Configure which courier statuses to follow up on"
          >
            <Columns size={16} />
            Courier Statuses
            {selectedCourierStatusIds.length > 0 && (
              <span className={styles.mappingBadge}>{selectedCourierStatusIds.length}</span>
            )}
          </button>
        }
      />

      <DataTable
        data={orders}
        columns={columns}
        actions={tableActions}
        sorting={sorting}
        onSortChange={setSorting}
        pagination={pagination}
        onPaginationChange={setPagination}
        loading={isLoading}
        emptyMessage="No orders found"
        emptyDescription="There are no orders matching the current follow-up statuses."
        emptyIcon={<Truck size={64} />}
      />

      {showStatusModal && (
        <DeliveryFollowUpStatusModal
          courierStatuses={courierStatuses}
          selectedIds={selectedCourierStatusIds}
          onClose={() => setShowStatusModal(false)}
          onSave={handleSaveStatusSettings}
          isSaving={isSavingSettings}
        />
      )}

      {addFollowUpOrder && (
        <AddDeliveryFollowUpModal
          orderId={addFollowUpOrder.id}
          onClose={handleCloseAddFollowUp}
          onSaved={handleFollowUpNoteSaved}
        />
      )}

      {historyOrder && (
        <DeliveryFollowUpHistoryModal
          orderId={historyOrder.id}
          onClose={handleCloseHistory}
        />
      )}

      {trackingOrder && (
        <AirwaybillTrackingModal
          orderId={trackingOrder.id}
          airwaybillNumber={trackingOrder.airwaybillNumber}
          courierName={trackingOrder.assignedCourierAccount?.accountName}
          onClose={() => setTrackingOrder(null)}
        />
      )}
    </div>
  );
};

export default DeliveryFollowUpPage;
